/**
 * SeoContentRenderer component
 *
 * Purpose:
 * - This component renders SEO-friendly hidden content (using "sr-only") to improve accessibility and SEO metadata.
 * - It safely renders the provided SEO content like name, descriptions, FAQs, etc.
 * - Ensures semantic HTML usage (h1, h2, h3, p) while keeping the content hidden from regular users but visible for screen readers and search engines.
 *
 * Usage:
 * - Pass a valid `SeoContent` object as `content` prop.
 * - Typically used inside a Tastic or page component to enrich the page with SEO metadata.
 */

import React from 'react';

type FAQ = {
  question?: string;
  answer?: string;
};

type SeoContent = {
  name?: string;
  topContentDescription?: { description: string };
  bottomContent?: { description: string }[];
  frequentlyAskedQuestions?: {
    questions?: FAQ[];
    overallTitle?: string;
  };
};

type SeoContentRendererProps = {
  content: SeoContent;
};

const SeoContentRenderer: React.FC<SeoContentRendererProps> = ({ content = {} }) => {
  const { name, topContentDescription, bottomContent, frequentlyAskedQuestions } = content;

  return (
    <>
      {/* h1 for the name */}
      {name && <h1 className="sr-only">{name}</h1>}

      {/* h2 for the overall title */}
      {frequentlyAskedQuestions?.overallTitle && <h2 className="sr-only">{frequentlyAskedQuestions.overallTitle}</h2>}
      {/* p for topContentDescription */}
      {topContentDescription?.description && <p className="sr-only">{topContentDescription.description}</p>}
      {/* h3 for each FAQ question */}
      {frequentlyAskedQuestions?.questions?.map((faq, index) => (
        <div key={index} className="sr-only">
          <h3 className="sr-only">{faq?.question}</h3>
          {faq?.answer && <p className="sr-only">{faq?.answer}</p>}
        </div>
      ))}

      {/* Optional paragraphs for bottom content */}
      {bottomContent?.map((item, index) => (
        <p key={index} className="sr-only">
          {item?.description}
        </p>
      ))}
    </>
  );
};

export default SeoContentRenderer;
